import {defineStore} from 'pinia'
import moment from 'moment/moment'
import api from '@/services/api'
import {useUserStore} from '@/stores/user'
import {useCategoriesStore} from '@/stores/categories'
import {useItemDataStore} from '@/stores/itemData'
import { useAppStore } from '@/stores/appStore'

export const useOdpReportsStore = defineStore('odpReports', {
    state: () => ({
        franchises: [],
        schools: [],
        reports: [
            {
                name: 'birthdayDates',
                label: 'Birthday dates'
            },
            {
                name: 'birthdayPartyEnquiries',
                label: 'Birthday party enquiries'
            },
            {
                name: 'courseDatesData',
                label: 'Course dates data'
            },
            {
                name: 'customerBookingData',
                label: 'Customer booking data'
            },
            {
                name: 'customerCredit',
                label: 'Customer credit'
            },
            {
                name: 'customerPostcodes',
                label: 'Customer postcodes'
            },
            {
                name: 'marketingReport',
                label: 'Marketing report'
            },
            {
                name: 'postcodes',
                label: 'Postcodes'
            },
            {
                name: 'partyBookings',
                label: 'Party bookings'
            },
            {
                name: 'referAFriendLTV',
                label: 'Refer a friend LTV'
            },
            {
                name: 'schoolParents',
                label: 'School parents'
            }
        ],
        formData: {
            reportName: '',
            startDate: '',
            endDate: ''
        },
        isLoading: false,
        rows: undefined
    }),
    
    getters: {
        startTimestamp: (state) => moment(state.formData.startDate, 'YYYY-MM-DD').unix(),
        endTimestamp: (state) => {
            // The date input type has no time, so equates to the time 00:00:00,
            // so add the number of seconds in a day minus 1 to get 23:59:59
            return moment(state.formData.endDate, 'YYYY-MM-DD').unix() + 86399
        },
        getDate: () => (timestamp) => {
            return timestamp ? moment(timestamp).format('DD/MM/YYYY') : ''
        },
        getAge: () => (dateOfBirth) => {
            if (!dateOfBirth) {
                return ''
            }
            
            const ageDifMs = Date.now() - new Date(dateOfBirth)
            const ageDate = new Date(ageDifMs) // milliseconds from epoch
            
            return Math.abs(ageDate.getUTCFullYear() - 1970)
        }
    },
    
    actions: {
        setFormData(formData) {
            this.formData = formData
        },
        setFranchisesState(franchises) {
            this.franchises = franchises
        },
        setSchoolsState(schools) {
            this.schools = schools
        },
        setRows(rows) {
            this.rows = rows
            this.isLoading = false
        },
        
        init({query} = {}) {
            const userStore = useUserStore()
            
            this.setFranchises()
            
            const franchiseId = userStore.user?.franchiseId ?? 0
            this.setSchools({franchiseId})
            
            // Support for copying and pasting URLs.
            // On load, if a query string has already been set then apply it to state.formData and load the rows
            if (
                query?.reportName // Only apply the query to formData if the query string has been set with vars
                && JSON.stringify(query) !== JSON.stringify(this.formData)
            ) {
                Object.assign(this.formData, query)
                this.formData.reportName = query.reportName
                this.formData.franchiseId = query.franchiseId ? parseInt(query.franchiseId) : 0
                this.loadRows()
            }
        },
        
        loadRows() {
            this.setRows(undefined)
            this.isLoading = true
            
            switch (this.formData.reportName) {
                case 'birthdayDates':
                    this.loadBirthdayDates()
                    break
                
                case 'birthdayPartyEnquiries':
                    this.loadBirthdayPartyEnquiries()
                    break
                
                case 'courseDatesData':
                    this.loadClubBookingData()
                    break
                
                case 'customerBookingData':
                    this.loadCustomerBookingData()
                    break
                
                case 'customerCredit':
                    this.loadCustomerCreditData()
                    break
                
                case 'customerPostcodes':
                    this.loadCustomers()
                    break
                
                case 'marketingReport':
                    this.loadBookingsReport()
                    break
                
                case 'postcodes':
                    this.loadCustomersPostcodes()
                    break
                
                case 'partyBookings':
                    this.loadPartyBookings()
                    break
                
                case 'referAFriendLTV':
                    this.loadReferAFriendLTV()
                    break
                
                case 'schoolParents':
                    this.loadSchoolsParents()
                    break
            }
        },
        
        async setFranchises() {
            const response = await api.get('api/component/odp_franchises', {
                params: {
                    sort: 'title',
                    fields: ['id', 'title']
                }
            })
            
            this.setFranchisesState(response.data.items)
        },
        
        async setSchools({franchiseId} = {}) {
            const params = {
                sort: 'name',
                fields: ['id', 'name'],
                name: {
                    ne: ''
                }
            }
            
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            
            const response = await api.get('api/component/odp_schools', {params})
            
            this.setSchoolsState(response.data.items)
        },
        
        async loadBirthdayDates() {
            const userStore = useUserStore()
            // Read franchiseId from Pinia userStore
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            const params = {
                status: 1
            }
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            
            // 1. Get school IDs
            const schoolResponse = await api.get('api/component/odp_schools/id', {params})
            
            // 2. Fetch children by school IDs and birth month
            const childrenResponse = await api.post('api/component/odp_children', {
                dateOfBirth: {
                    month: this.formData.month,
                },
                schoolId: schoolResponse.data.values
            }, {
                headers: {
                    'X-Http-Method-Override': 'GET'
                }
            })
            
            const children = childrenResponse.data.items
            
            // 3. Fetch parent category relations
            const parentIdsResponse = await api.post('api/categories/get-content-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                categoryId: children.map(a => a.id),
                groupByCategoryId: true
            }, {
                headers: {
                    'X-Http-Method-Override': 'GET'
                }
            })
            
            const childParentIds = parentIdsResponse.data
            const parentIds = Object.values(childParentIds).flat()
            
            // 4. Fetch parents data
            const parentsResponse = await api.post('api/component/m_registrants_registrants', {
                id: parentIds,
            }, {
                headers: {
                    'X-Http-Method-Override': 'GET'
                }
            })
            
            const allParents = parentsResponse.data.items
            
            // 5. Map into export/display rows
            const rows = children.map((item) => {
                let parentName = ''
                let parentEmail = ''
                let parentTelephone = ''
                let parentMobile = ''
                
                const parentIdsForItem = childParentIds[item.id]
                if (parentIdsForItem && parentIdsForItem.length > 0) {
                    const parentId = parentIdsForItem[0]
                    const parent = allParents.find(o => o.id === parentId)
                    if (parent) {
                        parentName = parent.displayName
                        parentEmail = parent.email
                        parentTelephone = parent.telephone
                        parentMobile = parent.mobile
                    }
                }
                
                return {
                    'Child name': item.displayName,
                    'DOB': this.getDate(item.dateOfBirth),
                    'Age': this.getAge(item.dateOfBirth),
                    'Parent name': parentName,
                    'Email': parentEmail,
                    'Telephone': parentTelephone,
                    'Telephone 2': parentMobile
                }
            })
            
            this.setRows(rows)
        },
        
        async loadBirthdayPartyEnquiries() {
            const userStore = useUserStore()
            const tableName = 'form_3' // Birthday party form
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            const franchisesObj = await api.get('api/component/odp_franchises')
            const franchises = franchisesObj.data.items
            
            const params = {
                createdDate: {
                    ge: this.startTimestamp,
                    le: this.endTimestamp
                }
            }
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            
            const enquiriesObj = await api.get('api/component/' + tableName, {
                params: params
            })
            
            const rows = enquiriesObj.data.items.map((item) => {
                const franchise = franchises.find(o => o.id === item.franchiseId) ?? {}
                
                return {
                    'ID': item.id,
                    'Created date': this.getDate(item.createdDate * 1000),
                    'First name': item.firstName,
                    'Last name': item.lastName,
                    'Email': item.email,
                    'Birthday child name': item.birthdayChildName,
                    'Preferred location': item.preferredLocation,
                    'Time': item.time,
                    'Preferred date': this.getDate(item.preferredDate),
                    'Main activity': item.mainActivity,
                    'Phone': item.phone,
                    'Birthday child age': item.birthdayChildAge,
                    'Number of children': item.numberOfChildren,
                    'Where did you hear about us': item.whereDidYouHearAboutUs,
                    'Franchise name': franchise.title
                }
            })
            
            this.setRows(rows)
        },
        
        async loadBookingsReport() {
            const userStore = useUserStore()
            const categoriesStore = useCategoriesStore()
            const itemDataStore = useItemDataStore()
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            const franchises = await itemDataStore.getAll({tableName: 'odp_franchises'})
            const allCourses = await itemDataStore.getAll({tableName: 'odp_courses'})
            const courses = allCourses.filter(o => !franchiseId || o.franchiseId === franchiseId)
            
            const {data: {items: courseDates}} = await api.get('api/component/odp_course_dates', {
                params: {courseId: courses.map(a => a.id)}
            })
            
            const {data: {items: bookings}} = await api.post('api/component/odp_course_bookings', {
                courseDateId: courseDates.map(a => a.id),
                createdDate: {
                    ge: this.startTimestamp,
                    le: this.endTimestamp,
                },
                hasExpired: 0,
                archived: 0,
                isArchived: 0,
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            
            if (!bookings.length) {
                this.setRows([])
                return
            }
            
            const {data: {items: creditItems}} = await api.post('api/component/odp_credit_log', {
                bookingId: bookings.map(a => a.id),
                debited: 1
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            if (!creditItems) {
                this.setRows([])
                return
            }
            
            const {data: {items: children}} = await api.post('api/component/odp_children', {
                id: bookings.map(a => a.childId),
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            if (!children) {
                this.setRows([])
                return
            }
            
            const {data: {items: schools}} = await api.post('api/component/odp_schools', {
                id: children.map(a => a.schoolId),
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            
            const childIds = children.map(a => a.id)
            const columnData = await categoriesStore.getTableColumnData({
                tableName: 'm_registrants_registrants',
                columnName: 'children'
            })
            const parentIds = columnData.filter(o => childIds.indexOf(o.catId) > -1).map(o => o.itemId)
            
            const {data: {items: parents}} = await api.post('api/component/m_registrants_registrants', {
                id: parentIds
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            
            const rows = []
            for (const k in bookings) {
                const item = bookings[k]
                
                const tableColumnData = await categoriesStore.getTableColumnData({
                    tableName: 'm_registrants_registrants',
                    columnName: 'children'
                })
                const parentId = tableColumnData.find(o => o.catId === item.childId)?.itemId
                const parent = parentId ? parents.find(o => o.id === parentId) : {}
                
                const child = children.filter(o => o.id === item.childId)[0] ?? {}
                const school = schools.filter(o => o.id === child.schoolId)[0] ?? {}
                const franchise = franchises.filter(o => o.id === school.franchiseId)[0] ?? {}
                
                switch (this.formData.reportName) {
                    case 'marketingReport':
                        rows.push({
                            'Date joined': this.getDate(parent.createdDate * 1000),
                            'Child name': child.displayName,
                            'Child DOB': this.getDate(child.dateOfBirth),
                            'School': school.name,
                            'Franchise': franchise.title,
                            'Booking ID': item.id
                        })
                        break
                }
            }
            
            this.setRows(rows)
        },
        
        async loadClubBookingData() {
            const userStore = useUserStore()
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            let params = {}
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            
            const courses = (await api.get('api/component/odp_courses', {params})).data.items
            
            const courseDates = (await api.get('api/component/odp_course_dates', {
                params: {
                    courseId: courses.map(a => a.id),
                    startDate: {ge: this.formData.startDate, le: this.formData.endDate}
                }
            })).data.items
            
            let eventIds = courseDates.filter(o => o.event).map(o => o.event)
            eventIds = [...new Set(eventIds)] // Unique
            
            const events = (await api.get('api/component/odp_event', {
                params: {id: eventIds, fields: ['id', 'name']}
            })).data.items
            
            const schools = (await api.post('api/component/odp_schools', {
                id: courseDates.map(a => a.schoolId)
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })).data.items
            
            const franchises = (await api.get('api/component/odp_franchises')).data.items
            
            const rows = []
            courseDates.forEach((item) => {
                const course = courses.filter(o => o.id === item.courseId)[0] ?? {}
                const school = schools.filter(o => o.id === item.schoolId)[0] ?? {}
                const franchise = franchises.filter(o => o.id === course.franchiseId)[0] ?? {}
                
                const startDate = moment(item.startDate)
                const event = events.find(o => o.id === item.event)
                
                rows.push({
                    'Course date ID': item.id,
                    'Franchise': franchise.title,
                    'Course': course.title,
                    'Event': event?.name || '',
                    'School': school.name,
                    'Location': item.location,
                    'Activity': item.activity,
                    'Start date': this.getDate(item.startDate),
                    'No of places': item.numberOfPlaces || 0,
                    'No of bookings': item.numberOfBookings || 0,
                    'Places remaining': item.placesRemaining || 0,
                    'Price': item.price,
                    'Published': item.status,
                    'Week no.': startDate.week(),
                    'Month no.': startDate.format('MM'),
                    'Year no.': startDate.format('YYYY'),
                })
            })
            
            this.setRows(rows)
        },
        
        async loadCustomerBookingData() {
            const userStore = useUserStore()
            const categoriesStore = useCategoriesStore()
            let rows = []
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            // Load the courses associated to the selected franchise
            let params = {}
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            const courses = (await api.get('api/component/odp_courses', {params})).data.items
            
            // Load the course dates associated to the courses
            const courseDates = (await api.get('api/component/odp_course_dates', {
                params: {courseId: courses.map(a => a.id)}
            })).data.items
            
            // Load the bookings associated to the course dates within the specified date range
            const bookings = (await api.post('api/component/odp_course_bookings', {
                courseDateId: courseDates.map(a => a.id),
                createdDate: {
                    ge: this.startTimestamp,
                    le: this.endTimestamp
                },
                hasExpired: 0,
                archived: 0,
                isArchived: 0
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })).data.items
            
            if (!bookings.length) {
                this.setRows(rows)
                return
            }
            
            let eventIds = courseDates.filter(o => o.event).map(o => o.event)
            eventIds = [...new Set(eventIds)] // Unique
            
            const events = (await api.get('api/component/odp_event', {
                params: {id: eventIds, fields: ['id', 'name']}
            })).data.items
            
            const children = (await api.post('api/component/odp_children', {
                id: bookings.map(a => a.childId),
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })).data.items
            
            let childIds = children.map(a => a.id)
            
            const catData = await categoriesStore.getTableColumnData({
                tableName: 'm_registrants_registrants',
                columnName: 'children'
            })
            const parentIds = catData.filter(o => childIds.indexOf(o.catId) > -1).map(o => o.itemId)
            
            const parents = (await api.post('api/component/m_registrants_registrants', {
                id: parentIds
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })).data.items
            
            const schools = (await api.post('api/component/odp_schools', {
                id: children.map(a => a.schoolId)
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })).data.items
            
            const franchises = (await api.get('api/component/odp_franchises')).data.items
            const franchiseVoucherCodes = (await api.get('api/component/franchise_voucher_code')).data.items
            const voucherProviders = (await api.get('api/component/voucher_provider')).data.items
            
            if (bookings.length) {
                const paymentLogItems = (await api.post('api/component/odp_bookings_payment_log', {
                    fields: ['bookingId', 'amount', 'label'],
                    bookingId: bookings.map(a => a.id)
                }, {
                    headers: {'X-Http-Method-Override': 'GET'}
                })).data.items
                
                const creditLogs = (await api.post('api/component/odp_credit_log', {
                    fields: ['bookingId', 'amount'],
                    bookingId: bookings.map(a => a.id),
                    debited: 1
                }, {
                    headers: {'X-Http-Method-Override': 'GET'}
                })).data.items
                
                for (const k in bookings) {
                    const item = bookings[k]
                    
                    const child = children.find(o => o.id === item.childId) ?? {}
                    const school = schools.find(o => o.id === child.schoolId) ?? {}
                    const courseDate = courseDates.find(o => o.id === item.courseDateId) ?? {}
                    const course = courses.find(o => o.id === courseDate.courseId) ?? {}
                    const franchise = franchises.find(o => o.id === course.franchiseId) ?? {}
                    const franchiseVoucherCode = franchiseVoucherCodes.find(o => o.id === item.franchiseVoucherCodeId) ?? {}
                    const voucherProvider = voucherProviders.find(o => o.id === franchiseVoucherCode.voucherProviderId) ?? {}
                    const referral = paymentLogItems.find(o => o.bookingId === item.id && o.label.includes('Referral code'))?.amount ?? 0
                    const paymentMade = paymentLogItems.find(o => o.bookingId === item.id && o.label.includes('Payment made'))?.amount ?? 0
                    const voucherPaid = paymentLogItems.find(o => o.bookingId === item.id && o.label.includes('Voucher paid'))?.amount ?? 0
                    const creditUsed = creditLogs.find(o => o.bookingId === item.id)?.amount ?? 0
                    
                    let parent = {}
                    const columnData = await categoriesStore.getTableColumnData({
                        tableName: 'm_registrants_registrants',
                        columnName: 'children'
                    })
                    const parentId = columnData.find(o => o.catId === item.childId)?.itemId
                    
                    if (parentId) {
                        parent = parents.find(o => o.id === parentId)
                    }
                    
                    const date = moment(courseDate.startDate)
                    const event = events.find(o => o.id === courseDate.event)
                    
                    rows.push({
                        'Booking ID': item.id,
                        'Event': event?.name || '',
                        'Course': course.title,
                        'Course date ID': courseDate.id,
                        'Course type': course.courseType,
                        'Start date': this.getDate(courseDate.startDate),
                        'Year (of club)': date.format('YYYY'),
                        'Week (of club)': date.week(),
                        'Month (of club)': date.format('MM'),
                        'School': school.name,
                        'Location': courseDate.location,
                        'Franchise': franchise.title,
                        'Payment note': item.paymentNote,
                        'Agreed price': item.agreedPrice,
                        'Amount paid': item.amountPaid || 0,
                        'Referral discount': referral || 0,
                        'Credit used': creditUsed || 0,
                        'Payment made': paymentMade || 0,
                        'Voucher paid': voucherPaid || 0,
                        'Voucher provider': voucherProvider.title,
                        'Franchise voucher code': franchiseVoucherCode.code,
                        'Name': parent.displayName || '',
                        'Email': parent.email || '',
                        'Child': child.displayName,
                        'Created': this.getDate(item.createdDate * 1000),
                    })
                }
            }
            
            this.setRows(rows)
        },
        
        async loadCustomerCreditData() {
            const userStore = useUserStore()
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            const schoolIds = (await api.get('api/component/odp_schools/id', {
                params: {
                    ...(franchiseId && {franchiseId})
                }
            })).data.values
            
            if (!schoolIds.length) return
            
            const childIds = (await api.post('api/component/odp_children/id', {
                schoolId: schoolIds,
                status: 1
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })).data.values
            
            if (!childIds.length) return
            
            const parentIds = (await api.post('api/categories/get-content-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                categoryId: childIds
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            }))?.data || []
            
            if (!parentIds.length) return
            
            const parentsChildIds = (await api.post('api/categories/get-category-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                contentId: parentIds,
                groupByItemId: true
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            }))?.data || []
            
            const childSchoolIds = ((await api.post('api/component/odp_children', {
                id: childIds,
                fields: ['id', 'schoolId']
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            }))?.data.items || [])
                .reduce((acc, {id, schoolId}) => {
                    acc[id] = schoolId
                    return acc
                }, {})
            
            const schoolFranchiseIds = ((await api.post('api/component/odp_schools', {
                id: schoolIds,
                fields: ['id', 'franchiseId']
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            }))?.data.items || [])
                .reduce((acc, {id, franchiseId}) => {
                    acc[id] = franchiseId
                    return acc
                }, {})
            
            const items = (await api.post('api/component/m_registrants_registrants', {
                id: parentIds,
                fields: ['id', 'firstName', 'lastName', 'email', 'creditAmount'],
                status: 1,
                creditAmount: {gt: 0}
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            }))?.data.items || []
            
            const franchises = (await api.get('api/component/odp_franchises')).data.items
            
            this.setRows(items.map(({id, firstName, lastName, email, creditAmount}) => {
                const childId = parentsChildIds[id][0] || 0
                const schoolId = childSchoolIds[childId] || 0
                const franchiseId = schoolFranchiseIds[schoolId] || 0
                const franchise = franchises.find(o => o.id === franchiseId)
                
                return {
                    'Customer ID': id,
                    'Franchise': franchise?.title || '',
                    'First name': firstName,
                    'Last name': lastName,
                    'Email': email,
                    'Credit amount': creditAmount
                }
            }))
        },
        
        async loadCustomersPostcodes() {
            const userStore = useUserStore()
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            let params = {
                status: 1
            }
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            
            const schoolsRes = await api.get('api/component/odp_schools/id', {params})
            
            const childrenRes = await api.get('api/component/odp_children/id', {
                params: {
                    schoolId: schoolsRes.data.values,
                    status: 1
                }
            })
            
            const parentIdsRes = await api.post('api/categories/get-content-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                categoryId: childrenRes.data.values
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            
            const parentIds = parentIdsRes.data
            
            const postcodesRes = await api.post('api/component/m_registrants_registrants/postcode', {
                id: parentIds,
                status: 1,
                sort: 'postcode'
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            
            let postcodes = []
            postcodesRes.data.values.forEach((postcode) => {
                if (postcode) {
                    postcode = postcode.toUpperCase().replace(/[^a-zA-Z0-9]/g, "").trim()
                    if (postcodes.indexOf(postcode) === -1) {
                        postcodes.push(postcode)
                    }
                }
            })
            
            let rows = postcodes.map((postcode) => ({
                'Postcode': postcode,
            }))
            
            this.setRows(rows)
        },
        
        async loadPartyBookings() {
            const userStore = useUserStore()
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            const params = {
                createdDate: {
                    ge: this.startTimestamp,
                    le: this.endTimestamp
                },
                isArchived: 0
            }
            
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            
            let o = await api.get('api/component/odp_party_bookings', {params})
            const items = o.data.items
            const customerIds = items.map(o => o.customerId)
            
            const rows = []
            if (!customerIds.length) {
                this.setRows(rows)
                return
            }
            
            o = await api.get('api/component/odp_franchises')
            const franchises = o.data.items
            
            o = await api.post('api/component/m_registrants_registrants', {
                id: customerIds
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            const customers = o.data.items
            
            items.forEach(item => {
                rows.push({
                    'Customer ID': item.customerId,
                    'Name': customers.find(o => o.id === item.customerId)?.name || '',
                    'Franchise': franchises.find(o => o.id === item.franchiseId)?.title || '',
                    'Party date': item.partyDate,
                    'Agreed price': item.agreedPrice,
                    'Notes': item.notes,
                    'Resend email': item.resendEmail
                })
            })
            
            this.setRows(rows)
        },
        
        async loadCustomers() {
            const userStore = useUserStore()
            const categoriesStore = useCategoriesStore()
            let parents
            let franchises
            let schools
            let children
            let childIds
            
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            let o = await api.post('api/component/m_registrants_registrants', {
                fields: ['id', 'firstName', 'lastName', 'email', 'telephone', 'mobile', 'postcode', 'createdDate',
                    'whereDidYouHearAboutUs', 'referredBy'],
                createdDate: {
                    ge: this.startTimestamp,
                    le: this.endTimestamp
                },
                status: 1,
                sort: 'lastName'
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            parents = o.data.items
            
            o = await api.post('api/categories/get-category-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                contentId: parents.map(a => a.id)
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            childIds = o.data
            
            o = await api.post('api/component/odp_children', {
                status: 1,
                id: childIds
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            children = o.data.items
            
            let params = {
                status: 1,
                id: children.map(o => o.schoolId)
            }
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            
            o = await api.post('api/component/odp_schools', params, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            schools = o.data.items
            
            o = await api.get('api/component/odp_franchises', {
                params: {
                    id: schools.map(o => o.franchiseId)
                }
            })
            franchises = o.data.items
            
            const referredByCustomerIds = parents.map(o => o.referredBy).filter(e => e)
            
            o = await api.get('api/component/m_registrants_registrants', {
                params: {
                    fields: ['id', 'email'],
                    id: referredByCustomerIds
                }
            })
            const referredByCustomerEmails = o.data.items
            
            const categoryData = await categoriesStore.getTableColumnData({
                tableName: 'm_registrants_registrants',
                columnName: 'children'
            })
            
            let rows = []
            for (const k in parents) {
                const parent = parents[k]
                
                let franchise = {}
                let child
                
                const objs = categoryData.filter(o => o.itemId === parent.id)
                
                objs.forEach(obj => {
                    if (child === undefined) {
                        const childId = obj.catId
                        child = children.find(o => o.id === childId)
                    }
                })
                if (child) {
                    const school = schools.find(o => o.id === child?.schoolId)
                    franchise = franchises.find(o => o.id === school?.franchiseId)
                }
                
                if (
                    !franchiseId
                    || franchise?.id === franchiseId
                ) {
                    const referredByCustomerEmail = referredByCustomerEmails
                        .find(o => o.id === parent.referredBy)
                    
                    rows.push({
                        'Customer ID': parent.id,
                        'Name': parent.firstName + ' ' + parent.lastName,
                        'Email': parent.email,
                        'Telephone': parent.telephone || parent.mobile,
                        'Postcode': parent.postcode,
                        'Franchise': franchise?.title || '',
                        'Where did you hear about us?': parent.whereDidYouHearAboutUs,
                        'Referred by': parent.referredBy || '',
                        'Referred by email': referredByCustomerEmail?.email || '',
                        'Created': this.getDate(parent.createdDate * 1000),
                    })
                }
            }
            
            this.setRows(rows)
        },
        
        async loadReferAFriendLTV() {
            const userStore = useUserStore()
            const appStore = useAppStore()
            
            let o
            // Read franchiseId from Pinia userStore
            const franchiseId = userStore.user?.franchiseId || this.formData.franchiseId
            
            const params = {fields: ['id', 'franchiseId']}
            if (franchiseId) {
                params.franchiseId = franchiseId
            }
            o = await api.get('api/component/odp_courses', {params})
            const courses = o.data.items
            const courseIds = courses.map(o => o.id)
            
            o = await api.get('api/component/odp_course_dates', {
                params: {
                    fields: ['id', 'courseId', 'startDate', 'event', 'location', 'activity'],
                    courseId: courseIds
                }
            })
            const courseDates = o.data.items
            const courseDateIds = courseDates.map(o => o.id)
            const eventIds = [...new Set(courseDates.map(o => o.event))]
            
            o = await api.get('api/component/odp_event', {
                params: {
                    fields: ['id', 'name'],
                    courseId: eventIds
                }
            })
            const events = o.data.items
            
            o = await api.get('api/component/m_registrants_registrants/id', {
                params: {
                    createdDate: {
                        ge: this.startTimestamp,
                        le: this.endTimestamp
                    },
                    referredBy: {gt: 0},
                    status: 1,
                    isArchived: 0
                }
            })
            const customerIds = o.data.values
            
            o = await api.post('api/categories/get-category-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                contentId: customerIds
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            const childIds = o.data
            
            o = await api.post('api/component/odp_course_bookings', {
                childId: childIds,
                courseDateId: courseDateIds,
                archived: 0,
                isArchived: 0,
                hasExpired: 0
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            const bookings = o.data.items.filter(o => o.placeConfirmed === 1 || o.voucherPaymentConfirmed === 1)
            
            if (!bookings?.length) {
                this.setRows([])
                return
            }
            
            const bookingsChildIds = bookings.map(o => o.childId)
            
            o = await api.post('api/categories/get-content-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                categoryId: bookingsChildIds
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            const bookingsCustomerIds = o.data
            
            o = await api.post('api/categories/get-category-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                contentId: bookingsCustomerIds,
                groupByItemId: true
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            const customersChildIds = o.data
            
            o = await api.post('api/component/odp_children', {
                id: childIds,
                fields: ['id', 'dateOfBirth', 'schoolId']
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            const children = o.data.items
            const schoolIds = [...new Set(children.map(o => o.schoolId))]
            
            o = await api.post('api/component/odp_schools', {
                id: schoolIds,
                fields: ['id', 'name']
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            const schools = o.data.items
            
            o = await api.post('api/component/m_registrants_registrants', {
                id: bookingsCustomerIds,
                status: 1,
                isArchived: 0
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            const customers = o.data.items
            
            o = await api.get('api/component/odp_franchises', {
                params: {
                    fields: ['id', 'title']
                }
            })
            const franchises = o.data.items
            
            const rows = []
            
            customers.forEach(o => {
                const childIds = customersChildIds[o.id]
                const childBookings = bookings.filter(o => childIds.indexOf(o.childId) > -1)
                const numBookings = childBookings.length
                const total = childBookings.reduce((total, o) => {
                    const value = parseFloat(o.amountPaid)
                    return value ? total + value : total
                }, 0)
                const totalStr = Math.round((total + Number.EPSILON) * 100) / 100
                const averagePaid = Math.round((total / numBookings) * 100) / 100
                
                const courseDateId = childBookings.map(o => o.courseDateId)[0]
                const courseId = courseDates.find(o => o.id === courseDateId)?.courseId
                const franchiseId = courses.find(o => o.id === courseId)?.franchiseId
                const franchiseTitle = franchises.find(o => o.id === franchiseId)?.title || ''
                
                const bookingIds = childBookings.map(o => o.id)
                let bookingLinks = ''
                if (bookingIds.length) {
                    bookingIds.forEach((id) => {
                        const href = (appStore.ccmsEndpoint || '') + '#/odp_course_bookings/' + id
                        bookingLinks += '<a href="' + href + '">' + id + '</a> '
                    })
                }
                
                const booking = childBookings[0]
                const courseDate = courseDates.find(o => o.id === booking.courseDateId)
                
                const childId = childIds[0]
                const child = children.find(o => o.id === childId)
                
                const row = {
                    'ID': o.id,
                    'Created date': this.getDate(o.createdDate * 1000),
                    'First name': o.firstName,
                    'Last name': o.lastName,
                    'Total paid bookings': '£' + totalStr,
                    'Number of bookings': numBookings,
                    'Average paid': '£' + averagePaid,
                    'Franchise': franchiseTitle,
                    'Course date': this.getDate(courseDate.startDate),
                    'Event': events.find(o => o.id === courseDate.event)?.name || '',
                    'Location': courseDate.location,
                    'Activity': courseDate.activity,
                    'Age of child': moment().diff(child.dateOfBirth, 'years'),
                    'School child attends': schools.find(o => o.id === child.schoolId)?.name,
                    'Bookings': bookingLinks
                }
                
                rows.push(row)
            })
            
            this.setRows(rows)
        },
        
        async loadSchoolsParents() {
            let children
            let childParentIds
            
            const schoolIdsRes = await api.get('api/component/odp_children/id', {
                params: {
                    schoolId: this.formData.schoolId
                }
            })
            const childIds = schoolIdsRes.data.values
            
            let params = {
                archived: 0,
                childId: childIds
            }
            if (this.formData.monthsAgo) {
                let time = moment(new Date()).subtract(this.formData.monthsAgo, 'month').unix()
                params.createdDate = {
                    ge: time
                }
            }
            
            const bookingsRes = await api.post('api/component/odp_course_bookings/childId', params, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            
            const childrenRes = await api.post('api/component/odp_children', {
                id: bookingsRes.data.values,
                schoolId: this.formData.schoolId,
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            children = childrenRes.data.items
            
            const contentIdsRes = await api.post('api/categories/get-content-ids', {
                tableName: 'm_registrants_registrants',
                columnName: 'children',
                categoryId: children.map(a => a.id),
                groupByCategoryId: true
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            childParentIds = contentIdsRes.data
            let parentIds = Object.values(childParentIds).flat()
            
            const parentsRes = await api.post('api/component/m_registrants_registrants', {
                id: parentIds,
            }, {
                headers: {'X-Http-Method-Override': 'GET'}
            })
            let allParents = parentsRes.data.items
            
            let rows = []
            children.forEach((item) => {
                let parentName = ''
                let parentEmail = ''
                let parentTelephone = ''
                let parentMobile = ''
                
                let parentIds = childParentIds[item.id]
                if (parentIds) {
                    let parentId = parentIds[0]
                    let parent = allParents.find(o => o.id === parentId)
                    if (parent) {
                        parentName = parent.displayName
                        parentEmail = parent.email
                        parentTelephone = parent.telephone
                        parentMobile = parent.mobile
                    }
                }
                
                rows.push({
                    'Parent name': parentName,
                    'Child name': item.displayName,
                    'Child DOB': this.getDate(item.dateOfBirth),
                    'Email': parentEmail,
                    'Telephone': parentTelephone,
                    'Telephone 2': parentMobile
                })
            })
            
            this.setRows(rows)
        }
    }
})